/**
 * 票链云 — 领域枚举（前后端共享的唯一真相源）
 *
 * 命名约定：枚举值统一使用 SCREAMING_SNAKE_CASE 英文字符串，
 * 中文展示名集中在各枚举旁的 *_LABELS 映射中，避免中文散落在代码各处。
 */
/* ==========================================================================
 * 一、平台级：租户、套餐、计费
 * ========================================================================== */
/** 租户生命周期状态（文档 3.1 租户生命周期管理） */
export const TenantStatus = {
    /** 已注册，待企业实名认证 */
    PENDING_VERIFY: 'PENDING_VERIFY',
    /** 认证通过，正常使用 */
    ACTIVE: 'ACTIVE',
    /** 已停用（欠费或违规），数据保留 */
    SUSPENDED: 'SUSPENDED',
    /** 已注销，数据归档 */
    ARCHIVED: 'ARCHIVED',
};
export const TENANT_STATUS_LABELS = {
    PENDING_VERIFY: '待认证',
    ACTIVE: '正常',
    SUSPENDED: '已停用',
    ARCHIVED: '已注销',
};
/**
 * 多租户隔离模式（文档 2.2 多租户模型选型）
 * 默认共享库 + tenant_id + RLS；中大型租户可升级为独立 Schema 或独立库。
 */
export const IsolationMode = {
    /** 共享库共享表，靠 tenant_id + 行级安全策略隔离 */
    SHARED: 'SHARED',
    /** 共享库独立 Schema */
    SCHEMA: 'SCHEMA',
    /** 独立数据库 */
    DATABASE: 'DATABASE',
};
export const ISOLATION_MODE_LABELS = {
    SHARED: '共享库（tenant_id 隔离）',
    SCHEMA: '独立 Schema',
    DATABASE: '独立数据库',
};
/** 套餐档位（文档 9.1 套餐设计） */
export const PlanTier = {
    /** 入门版 — 小微企业，共享库、基础 OCR、有限调用量 */
    STARTER: 'STARTER',
    /** 标准版 — 中型企业，独立 Schema、完整校验、T+ 对接 */
    STANDARD: 'STANDARD',
    /** 专业版 — 大型企业，独立库、专属流程、专属接口配额 */
    PROFESSIONAL: 'PROFESSIONAL',
    /** 定制版 — 集团客户，私有化部署、定制开发、SLA */
    ENTERPRISE: 'ENTERPRISE',
};
export const PLAN_TIER_LABELS = {
    STARTER: '入门版',
    STANDARD: '标准版',
    PROFESSIONAL: '专业版',
    ENTERPRISE: '定制版',
};
/** 订阅状态 */
export const SubscriptionStatus = {
    /** 试用中 */
    TRIALING: 'TRIALING',
    ACTIVE: 'ACTIVE',
    /** 已过期 */
    EXPIRED: 'EXPIRED',
    /** 已取消（到期后失效） */
    CANCELLED: 'CANCELLED',
};
export const SUBSCRIPTION_STATUS_LABELS = {
    TRIALING: '试用中',
    ACTIVE: '生效中',
    EXPIRED: '已过期',
    CANCELLED: '已取消',
};
/** 计费模式（文档 9.2 计费模式） */
export const BillingMode = {
    /** 订阅制，按年/月 */
    SUBSCRIPTION: 'SUBSCRIPTION',
    /** 调用量计费，按次 */
    USAGE: 'USAGE',
    /** 模块化授权 */
    MODULE: 'MODULE',
};
/* ==========================================================================
 * 二、用户与权限
 * ========================================================================== */
export const UserStatus = {
    INVITED: 'INVITED',
    ACTIVE: 'ACTIVE',
    DISABLED: 'DISABLED',
    LOCKED: 'LOCKED',
};
export const USER_STATUS_LABELS = {
    INVITED: '已邀请',
    ACTIVE: '正常',
    DISABLED: '已停用',
    LOCKED: '已锁定',
};
/**
 * 数据权限范围（文档 4.2 权限模型）
 * 在 Repository 层自动改写查询范围，而非依赖各业务方法自觉过滤。
 */
export const DataScope = {
    /** 仅本人 */
    SELF: 'SELF',
    /** 本部门及以下 */
    DEPT: 'DEPT',
    /** 本租户全部 */
    TENANT: 'TENANT',
    /** 自定义范围（部门/用户组合） */
    CUSTOM: 'CUSTOM',
};
export const DATA_SCOPE_LABELS = {
    SELF: '仅本人',
    DEPT: '本部门',
    TENANT: '本租户',
    CUSTOM: '自定义范围',
};
/**
 * 默认角色模板（文档 4.1 角色体系）
 * 租户开通时播种，租户可在此基础上增删改。
 */
export const DefaultRoleCode = {
    /** 租户管理员 — 租户内全部配置与用户管理 */
    TENANT_ADMIN: 'TENANT_ADMIN',
    /** 项目部经办人 — 上传发票、关联合同、处理退回 */
    PROJECT_HANDLER: 'PROJECT_HANDLER',
    /** 财务初审岗 — 审阅校验报告、人工复核、确认推送 */
    FINANCE_REVIEWER: 'FINANCE_REVIEWER',
    /** 财务记账岗 — T+ 端选择科目、生成凭证、回写 */
    FINANCE_BOOKKEEPER: 'FINANCE_BOOKKEEPER',
    /** 法务岗 — 合同风险、归档、事后抽查 */
    LEGAL: 'LEGAL',
    /** 技术实施组 — 接口配置、日志查看、异常处理 */
    TECH_OPS: 'TECH_OPS',
    // —— 合同全生命周期与费用报销新增（2026-09-23）——
    /** 经营部经办 — 合同登记、变更、提交审批 */
    BUSINESS_HANDLER: 'BUSINESS_HANDLER',
    /** 部门负责人 — 费用申请与报销的一级审批（业务真实性） */
    DEPT_HEAD: 'DEPT_HEAD',
    /** 领导 — 超金额阈值的终审 */
    LEADER: 'LEADER',
    /** 出纳 — 付款执行 */
    CASHIER: 'CASHIER',
    /** 普通员工 — 只能看本人单据 */
    EMPLOYEE: 'EMPLOYEE',
};
export const DEFAULT_ROLE_LABELS = {
    TENANT_ADMIN: '租户管理员',
    PROJECT_HANDLER: '项目部经办人',
    FINANCE_REVIEWER: '财务初审岗',
    FINANCE_BOOKKEEPER: '财务记账岗',
    LEGAL: '法务岗',
    TECH_OPS: '技术实施组',
    BUSINESS_HANDLER: '经营部经办',
    DEPT_HEAD: '部门负责人',
    LEADER: '领导',
    CASHIER: '出纳',
    EMPLOYEE: '普通员工',
};
/**
 * 功能权限码（文档 4.2）
 * 格式 `资源:动作`，同时用于菜单、按钮与接口级鉴权。
 */
export const PermissionCode = {
    // 租户与组织
    TENANT_CONFIG_READ: 'tenant:config:read',
    TENANT_CONFIG_WRITE: 'tenant:config:write',
    USER_READ: 'user:read',
    USER_WRITE: 'user:write',
    ROLE_READ: 'role:read',
    ROLE_WRITE: 'role:write',
    DEPT_READ: 'dept:read',
    DEPT_WRITE: 'dept:write',
    // 流程配置
    FLOW_TEMPLATE_READ: 'flow:template:read',
    FLOW_TEMPLATE_WRITE: 'flow:template:write',
    // 发票
    INVOICE_CREATE: 'invoice:create',
    INVOICE_READ: 'invoice:read',
    INVOICE_UPDATE_OCR: 'invoice:ocr:update',
    INVOICE_SUBMIT: 'invoice:submit',
    INVOICE_DELETE: 'invoice:delete',
    /**
     * 上传/删除发票复印件附件，以及修正提取出的手写内容。
     *
     * 【为什么不复用 INVOICE_CREATE】
     * 上传复印件与开票是两件事：复印件上常有签字、批注这类**会被系统当数据存下来**
     * 的内容，将来若要按岗位收紧（例如只让财务补传、不让经办人改签收人），
     * 拆开的成本远低于事后从 INVOICE_CREATE 里剥离。
     * 权限每次请求实时加载，收回是下一个请求就生效的，粒度细没有额外代价。
     */
    INVOICE_ATTACHMENT_WRITE: 'invoice:attachment:write',
    /** 查看发票复印件与提取出的手写内容 */
    INVOICE_ATTACHMENT_READ: 'invoice:attachment:read',
    // 校验
    CHECK_READ: 'check:read',
    CHECK_RETRY: 'check:retry',
    /** 外部校验不可用时人工接管并留痕（文档七 风险管控） */
    CHECK_MANUAL_OVERRIDE: 'check:manual-override',
    // 审核
    INVOICE_REVIEW: 'invoice:review',
    /** 红冲/作废等高风险票的专项放行（文档 4.3 特例放行） */
    INVOICE_SPECIAL_RELEASE: 'invoice:special-release',
    // 记账与 T+
    TPLUS_PUSH: 'tplus:push',
    TPLUS_READ: 'tplus:read',
    /** 进项锁定为不可逆写操作，独立权限且默认不给 */
    TAX_INPUT_LOCK: 'tax:input-lock',
    // 合同
    CONTRACT_READ: 'contract:read',
    CONTRACT_WRITE: 'contract:write',
    /**
     * 合同生命周期的分段审批权限。
     *
     * 【为什么按岗位拆成四个码，而不是一个 contract:approve】
     * 需求 3.2 的审批链是 经营部 → 财务 → 法务 → 领导，四段看的是不同的东西：
     * 经营部看条款与结算方式、财务看金额税号发票条款、法务看条款风险、领导按金额阈值兜底。
     * 合成一个码就等于"任一段的人都能替另外三段签字"，而这四段的判断依据并不相通。
     * 权限每次请求实时加载，拆细没有额外代价。
     */
    CONTRACT_REVIEW: 'contract:review',
    CONTRACT_FINANCE_REVIEW: 'contract:finance-review',
    CONTRACT_LEGAL_REVIEW: 'contract:legal-review',
    CONTRACT_APPROVE: 'contract:approve',
    /** 登记付款申请、确认付款（付款是不可逆的资金动作，独立于登记） */
    CONTRACT_PAYMENT_WRITE: 'contract:payment:write',
    /** 供应商/分包商履约评价 */
    CONTRACT_EVALUATE: 'contract:evaluate',
    /** 导出台账（导出会写审计，与登记分离） */
    CONTRACT_EXPORT: 'contract:export',
    /** 处理合同预警：确认或忽略（忽略也要填理由，留痕） */
    CONTRACT_ALERT_HANDLE: 'contract:alert:handle',
    // 费用报销
    EXPENSE_READ: 'expense:read',
    /** 事前费用申请 */
    EXPENSE_APPLY: 'expense:apply',
    /** 事后报销提交 */
    EXPENSE_REIMBURSE: 'expense:reimburse',
    /** 部门负责人审批：业务真实性 */
    EXPENSE_DEPT_APPROVE: 'expense:dept-approve',
    /** 财务初审：发票合规、金额、预算 */
    EXPENSE_REVIEW: 'expense:review',
    /** 财务复核：大额或异常 */
    EXPENSE_RECHECK: 'expense:recheck',
    /** 领导审批：超金额阈值 */
    EXPENSE_APPROVE: 'expense:approve',
    /** 出纳付款执行 */
    EXPENSE_PAY: 'expense:pay',
    /** 预算编制与调整 */
    EXPENSE_BUDGET_MANAGE: 'expense:budget:manage',
    EXPENSE_REPORT: 'expense:report',
    EXPENSE_EXPORT: 'expense:export',
    EXPENSE_ALERT_HANDLE: 'expense:alert:handle',
    // 档案
    ARCHIVE_READ: 'archive:read',
    ARCHIVE_EXPORT: 'archive:export',
    // 接口配置与日志
    PROVIDER_CONFIG_READ: 'provider:config:read',
    PROVIDER_CONFIG_WRITE: 'provider:config:write',
    PROVIDER_LOG_READ: 'provider:log:read',
    // 消息
    MESSAGE_READ: 'message:read',
};
/* ==========================================================================
 * 三、发票
 * ========================================================================== */
/**
 * 发票类型代码
 *
 * 取值与税务/OCR 通道保持一致（阿里云 VerifyVATInvoice 的发票类型代码表），
 * 因为它决定「查验时需要提供哪些要素」——这是查验失败的头号原因。
 */
export const InvoiceTypeCode = {
    /** 增值税专用发票 */
    VAT_SPECIAL: '01',
    /** 货物运输业增值税专用发票 */
    FREIGHT_SPECIAL: '02',
    /** 机动车销售统一发票 */
    VEHICLE_SALE: '03',
    /** 增值税普通发票 */
    VAT_GENERAL: '04',
    /** 增值税普通发票（电子） */
    VAT_ELECTRONIC: '10',
    /** 增值税普通发票（卷式） */
    VAT_ROLL: '11',
    /** 增值税普通发票（通行费） */
    VAT_TOLL: '14',
    /** 二手车销售统一发票 */
    USED_VEHICLE: '15',
    /** 增值税电子专用发票 */
    VAT_E_SPECIAL: '20',
    /** 数电发票（增值税专用发票） */
    E_INVOICE_SPECIAL: '31',
    /** 数电发票（增值税普通发票） */
    E_INVOICE_GENERAL: '32',
    /** 电子发票（铁路电子客票） */
    E_TRAIN_TICKET: '51',
    /** 电子发票（航空运输电子客票行程单） */
    E_AIR_TICKET: '61',
    /** 电子发票（机动车销售统一发票） */
    E_VEHICLE_SALE: '83',
    /** 电子发票（二手车销售统一发票） */
    E_USED_VEHICLE: '84',
    /** 数电发票（纸质专用发票） */
    E_PAPER_SPECIAL: '85',
    /** 数电发票（纸质普通发票） */
    E_PAPER_GENERAL: '86',
    /** 数电纸质发票（机动车销售统一发票） */
    E_PAPER_VEHICLE: '87',
    /** 数电纸质发票（二手车销售统一发票） */
    E_PAPER_USED_VEHICLE: '88',
    /** 未识别 / 其他 */
    UNKNOWN: '99',
};
export const INVOICE_TYPE_LABELS = {
    '01': '增值税专用发票',
    '02': '货物运输业增值税专用发票',
    '03': '机动车销售统一发票',
    '04': '增值税普通发票',
    '10': '增值税普通发票（电子）',
    '11': '增值税普通发票（卷式）',
    '14': '增值税普通发票（通行费）',
    '15': '二手车销售统一发票',
    '20': '增值税电子专用发票',
    '31': '数电发票（增值税专用发票）',
    '32': '数电发票（增值税普通发票）',
    '51': '电子发票（铁路电子客票）',
    '61': '电子发票（航空运输电子客票行程单）',
    '83': '电子发票（机动车销售统一发票）',
    '84': '电子发票（二手车销售统一发票）',
    '85': '数电发票（纸质专用发票）',
    '86': '数电发票（纸质普通发票）',
    '87': '数电纸质发票（机动车销售统一发票）',
    '88': '数电纸质发票（二手车销售统一发票）',
    '99': '其他/未识别',
};
/**
 * 数电票系列 —— 无发票代码，发票号码为 20 位。
 * 校验层必须兼容此差异，否则全电发票会被误判为格式错误。
 */
export const E_INVOICE_TYPE_CODES = [
    '31', '32', '51', '61', '83', '84', '85', '86',
];
/** 发票业务生命周期状态（平台内部状态机） */
export const InvoiceStatus = {
    /** 已上传，待提交校验 */
    DRAFT: 'DRAFT',
    /** 自动校验执行中（OCR / 企查查 / 税务局 / 合同比对） */
    CHECKING: 'CHECKING',
    /** 校验完成，待财务初审 */
    PENDING_REVIEW: 'PENDING_REVIEW',
    /** 财务初审退回，待经办人处理 */
    REJECTED: 'REJECTED',
    /** 初审通过，待推送 T+ */
    APPROVED: 'APPROVED',
    /** 已推送至 T+ 待办业务池（尚未生成凭证） */
    PUSHED: 'PUSHED',
    /** T+ 已生成凭证并回写 */
    BOOKED: 'BOOKED',
    /** 已归档 */
    ARCHIVED: 'ARCHIVED',
    /** 流程终止（作废/主动废弃） */
    TERMINATED: 'TERMINATED',
};
export const INVOICE_STATUS_LABELS = {
    DRAFT: '待提交',
    CHECKING: '校验中',
    PENDING_REVIEW: '待初审',
    REJECTED: '已退回',
    APPROVED: '初审通过',
    PUSHED: '已推送T+',
    BOOKED: '已记账',
    ARCHIVED: '已归档',
    TERMINATED: '已终止',
};
/** 发票有效性判定（由验真结果派生） */
export const InvoiceValidity = {
    /** 查验成功且票面一致 */
    VALID: 'VALID',
    /** 已作废 */
    VOIDED: 'VOIDED',
    /** 已（全额）冲红 */
    RED_FLUSHED: 'RED_FLUSHED',
    /** 部分冲红 */
    PARTIAL_RED: 'PARTIAL_RED',
    /** 票面要素与税务记录不一致 */
    MISMATCH: 'MISMATCH',
    /** 查无此票 */
    NOT_FOUND: 'NOT_FOUND',
    /** 尚未查验或查验不可用 */
    UNKNOWN: 'UNKNOWN',
};
export const INVOICE_VALIDITY_LABELS = {
    VALID: '有效',
    VOIDED: '已作废',
    RED_FLUSHED: '已冲红',
    PARTIAL_RED: '部分冲红',
    MISMATCH: '要素不一致',
    NOT_FOUND: '查无此票',
    UNKNOWN: '待查验',
};
/* ==========================================================================
 * 四、校验与风险
 * ========================================================================== */
/**
 * 单项校验的执行状态。
 * UNAVAILABLE 对应文档七「接口不可用风险：标记『外部校验暂不可用』，
 * 流程允许人工临时接管，不卡死业务」。
 */
export const CheckStatus = {
    /** 尚未执行 */
    PENDING: 'PENDING',
    /** 执行中 */
    RUNNING: 'RUNNING',
    /** 校验成功 */
    SUCCESS: 'SUCCESS',
    /** 校验执行失败（接口报错） */
    FAILED: 'FAILED',
    /** 外部接口不可用，标记待人工接管 */
    UNAVAILABLE: 'UNAVAILABLE',
    /** 已由人工接管并填写核验说明 */
    MANUAL_OVERRIDE: 'MANUAL_OVERRIDE',
};
export const CHECK_STATUS_LABELS = {
    PENDING: '待校验',
    RUNNING: '校验中',
    SUCCESS: '校验成功',
    FAILED: '校验失败',
    UNAVAILABLE: '外部校验暂不可用',
    MANUAL_OVERRIDE: '人工接管',
};
/** 风险等级（文档 5.5） */
export const RiskLevel = {
    /** 红冲、作废、供应商注销/停业 — 默认禁止流转 T+ */
    HIGH: 'HIGH',
    /** 超经营范围、进项锁定失败、合同不一致 — 提示不阻断 */
    MEDIUM: 'MEDIUM',
    /** OCR 置信度低、字段缺失 */
    LOW: 'LOW',
    NONE: 'NONE',
};
export const RISK_LEVEL_LABELS = {
    HIGH: '高风险',
    MEDIUM: '中风险',
    LOW: '低风险',
    NONE: '无风险',
};
/** 风险等级排序权重，用于取最大值 */
export const RISK_LEVEL_WEIGHT = {
    NONE: 0,
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
};
/** 风险项类型 */
export const RiskItemCode = {
    /** 发票已冲红 */
    RED_FLUSHED: 'RED_FLUSHED',
    /** 发票已作废 */
    VOIDED: 'VOIDED',
    /** 部分冲红 */
    PARTIAL_RED: 'PARTIAL_RED',
    /** 供应商已注销/吊销/停业 */
    SUPPLIER_ABNORMAL: 'SUPPLIER_ABNORMAL',
    /** 开票项目超出经营范围 */
    SCOPE_MISMATCH: 'SCOPE_MISMATCH',
    /** 发票与关联合同信息不一致 */
    CONTRACT_MISMATCH: 'CONTRACT_MISMATCH',
    /** 发票要素与税务记录不一致 */
    TAX_MISMATCH: 'TAX_MISMATCH',
    /** 查无此票 */
    TAX_NOT_FOUND: 'TAX_NOT_FOUND',
    /** OCR 识别置信度低 */
    OCR_LOW_CONFIDENCE: 'OCR_LOW_CONFIDENCE',
    /** 关键字段缺失 */
    FIELD_MISSING: 'FIELD_MISSING',
    /** 该张发票当日查验次数已超限 */
    TAX_QUERY_LIMIT: 'TAX_QUERY_LIMIT',
    /** 外部校验不可用，需人工核验 */
    CHECK_UNAVAILABLE: 'CHECK_UNAVAILABLE',
    /** 缺少合同关联 */
    CONTRACT_MISSING: 'CONTRACT_MISSING',
    /**
     * 识别结果被人工修正，但校验结果仍基于修正前的票面。
     *
     * 【为什么必须单独成一个风险项】
     * 财务初审看到的是"校验通过"，但那份校验是用 OCR 认错的票号去税务系统查的。
     * 若不提示，初审会基于与票面不符的校验结论放行——这比没有校验更危险，
     * 因为它带着"已核验"的假象。
     */
    CHECK_STALE: 'CHECK_STALE',
    // —— 费用报销专属（需求 3.3 的八项校验里，票链云没有的四项）——
    /**
     * 重复报销：这张发票已经挂在另一张未释放的报销单上。
     *
     * 【为什么是高风险】
     * 一票多报是**主观套现**，不是数据质量问题。数据库层已有部分唯一索引
     * 兜底（`expense_invoice_rel` 上 `WHERE released_at IS NULL`），
     * 这个风险项用于解释"为什么被挡下"，以及给历史遗留数据留一条可见的路。
     */
    EXPENSE_DUPLICATE: 'EXPENSE_DUPLICATE',
    /** 发票抬头不是本企业 */
    EXPENSE_BUYER_MISMATCH: 'EXPENSE_BUYER_MISMATCH',
    /** 报销金额与发票价税合计不一致 */
    EXPENSE_AMOUNT_MISMATCH: 'EXPENSE_AMOUNT_MISMATCH',
    /** 开票品类与所选费用类型不符（如餐饮票报成差旅费） */
    EXPENSE_CATEGORY_MISMATCH: 'EXPENSE_CATEGORY_MISMATCH',
    /** 累计费用已超预算 */
    EXPENSE_BUDGET_EXCEEDED: 'EXPENSE_BUDGET_EXCEEDED',
};
export const RISK_ITEM_LABELS = {
    RED_FLUSHED: '发票已冲红',
    VOIDED: '发票已作废',
    PARTIAL_RED: '发票部分冲红',
    SUPPLIER_ABNORMAL: '供应商经营异常',
    SCOPE_MISMATCH: '开票项目超出经营范围',
    CONTRACT_MISMATCH: '发票与合同不一致',
    TAX_MISMATCH: '票面与税务记录不一致',
    TAX_NOT_FOUND: '税务系统查无此票',
    OCR_LOW_CONFIDENCE: 'OCR 识别置信度低',
    FIELD_MISSING: '关键字段缺失',
    TAX_QUERY_LIMIT: '当日查验次数超限',
    CHECK_UNAVAILABLE: '外部校验暂不可用',
    CONTRACT_MISSING: '未关联合同',
    CHECK_STALE: '校验结果已过期',
    EXPENSE_DUPLICATE: '该发票已用于其他报销单',
    EXPENSE_BUYER_MISMATCH: '发票抬头不是本企业',
    EXPENSE_AMOUNT_MISMATCH: '报销金额与发票金额不一致',
    EXPENSE_CATEGORY_MISMATCH: '开票品类与费用类型不符',
    EXPENSE_BUDGET_EXCEEDED: '已超出预算',
};
/* ==========================================================================
 * 五、流程引擎
 * ========================================================================== */
export const FlowNodeType = {
    /** 流程起点 */
    START: 'START',
    /** 自动节点（调用校验编排等系统动作） */
    AUTO: 'AUTO',
    /** 人工审批节点 */
    APPROVAL: 'APPROVAL',
    /** 条件分支 */
    CONDITION: 'CONDITION',
    /** 推送 T+ 待办业务池 */
    PUSH: 'PUSH',
    /** 流程终点 */
    END: 'END',
};
export const FLOW_NODE_TYPE_LABELS = {
    START: '开始',
    AUTO: '自动处理',
    APPROVAL: '人工审批',
    CONDITION: '条件分支',
    PUSH: '推送T+',
    END: '结束',
};
export const FlowInstanceStatus = {
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    /** 被退回至经办人（流程未终结，等待重新提交） */
    RETURNED: 'RETURNED',
    TERMINATED: 'TERMINATED',
};
export const FLOW_INSTANCE_STATUS_LABELS = {
    RUNNING: '进行中',
    COMPLETED: '已完成',
    RETURNED: '已退回',
    TERMINATED: '已终止',
};
export const FlowTaskStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    /** 超时未处理 */
    TIMEOUT: 'TIMEOUT',
    CANCELLED: 'CANCELLED',
};
export const FLOW_TASK_STATUS_LABELS = {
    PENDING: '待处理',
    APPROVED: '已通过',
    REJECTED: '已退回',
    TIMEOUT: '已超时',
    CANCELLED: '已取消',
};
/** 审批动作 —— 退回规则见文档 4.3 */
export const FlowAction = {
    /** 通过，流转至下一节点 */
    APPROVE: 'APPROVE',
    /** 退回至经办人（重新提交） */
    REJECT_TO_HANDLER: 'REJECT_TO_HANDLER',
    /** 退回至上一节点 */
    REJECT_TO_PREV: 'REJECT_TO_PREV',
    /** 终止流程 */
    TERMINATE: 'TERMINATE',
    /**
     * 特例放行 —— 红冲/作废等高风险发票的专项审批通道。
     * 文档 4.3：红冲/作废发票需人工专项审批并留痕。
     */
    SPECIAL_RELEASE: 'SPECIAL_RELEASE',
};
export const FLOW_ACTION_LABELS = {
    APPROVE: '通过',
    REJECT_TO_HANDLER: '退回经办人',
    REJECT_TO_PREV: '退回上一节点',
    TERMINATE: '终止流程',
    SPECIAL_RELEASE: '特例放行',
};
/* ==========================================================================
 * 六、外部接口
 * ========================================================================== */
/** 外部接口供应商标识 */
export const ProviderCode = {
    /** 发票识别 OCR */
    OCR: 'OCR',
    /** 发票验真 */
    TAX_VERIFY: 'TAX_VERIFY',
    /** 企查查 */
    QCC: 'QCC',
    /** 畅捷通 T+ */
    TPLUS: 'TPLUS',
    /** 钉钉 */
    DINGTALK: 'DINGTALK',
    /** 短信 */
    SMS: 'SMS',
    /**
     * 合同附件解析（扫描件通道）。
     *
     * 【为什么不复用 OCR】
     * `OCR` 指阿里云 `RecognizeInvoice`，它是**发票专用**接口，认不出通用合同版式；
     * 合同扫描件走的是 `RecognizeAllText`（全文识别），在阿里云是**另一个独立服务**，
     * 开通状态可以与发票 OCR 不同——完全可能出现"发票识别能用、合同扫描件不能用"。
     * 复用 OCR 会让管理端的接口日志把两者混为一谈，排查时指向错误的方向。
     */
    CONTRACT_EXTRACT: 'CONTRACT_EXTRACT',
    /**
     * 发票附件手写体识别（复印件上的签字、日期、批注）。
     *
     * 【为什么不复用 OCR】
     * `OCR` 指阿里云 `RecognizeInvoice`，它是**发票专用**接口，只认票面版式，
     * 提取不了手写内容；手写走的是 `RecognizeHandwriting`（手写体识别），
     * 在阿里云**又是一个独立服务**，开通状态与发票 OCR 互不相干——
     * 完全可能出现"电子发票识别正常、手写内容识别不能用"。
     * 复用 OCR 会让管理端的接口日志把两者混为一谈，排查时指向错误的方向。
     */
    INVOICE_HANDWRITING: 'INVOICE_HANDWRITING',
};
export const PROVIDER_LABELS = {
    OCR: '发票识别',
    TAX_VERIFY: '发票验真',
    QCC: '企查查',
    TPLUS: '畅捷通T+',
    DINGTALK: '钉钉',
    SMS: '短信',
    CONTRACT_EXTRACT: '合同解析',
    INVOICE_HANDWRITING: '手写识别',
};
/** 适配器运行模式 */
export const ProviderMode = {
    /** 真实调用外部接口 */
    REAL: 'REAL',
    /**
     * 本地解析：数据真实，但不产生任何外部调用。
     *
     * 目前只有 PDF 文本层提取用这个值。它与 REAL 必须区分开——
     * 本地解析**没有第三方原始报文可归档**，而"原始报文归档"是
     * 对外部调用的审计要求（文档 8.3）。标成 REAL 会在"税务争议时
     * 能拿出什么证据"这件事上给出错误暗示，也会让用量看板把
     * 零成本的本地解析算成付费调用。
     */
    LOCAL: 'LOCAL',
    /** 沙箱模拟，不产生外部调用与费用。**只应在显式配置 `*_DRIVER=sandbox` 时出现** */
    SANDBOX: 'SANDBOX',
    /** 人工兜底（外部接口不可用时） */
    MANUAL: 'MANUAL',
    /**
     * 通道不可用：本应走外部接口，但因未配置/已停用而不能走。
     *
     * 这是"缺凭证不再静默降级为沙箱"的落点。用 MANUAL 表达这件事是不行的：
     * MANUAL 的语义是"有意选择人工"，而漏配凭证是**事故**，
     * 混为一谈会让配置错误再次伪装成正常状态。
     */
    UNAVAILABLE: 'UNAVAILABLE',
};
export const PROVIDER_MODE_LABELS = {
    REAL: '真实调用',
    LOCAL: '本地解析',
    SANDBOX: '沙箱模拟',
    MANUAL: '人工兜底',
    UNAVAILABLE: '通道未启用',
};
export const PROVIDER_MODE_DESCRIPTIONS = {
    REAL: '真实对外调用，产生费用并计入配额；返回结果参与风险判定',
    LOCAL: '本地解析，数据真实、不产生外部调用与费用，但无第三方原始报文可归档',
    SANDBOX: '使用沙箱模拟数据，不产生外部调用与费用；**结果不可作为业务凭据**',
    MANUAL: '不调用外部接口，全部转人工核验；适用于接口故障或未接入的场景',
    UNAVAILABLE: '通道未配置或已停用，本次未执行真实校验；需人工接管或补齐部署配置',
};
/**
 * 由已落库的通道名反推运行模式。
 *
 * 用途有二：
 *   1. 验真命中 24h 缓存时没有本次调用结果，只能从缓存行的 `channel` 反推——
 *      这比"读当前配置"更真实，因为缓存行可能是通道切换**之前**写下的。
 *   2. 前端展示历史数据时做同样的映射。
 *
 * 认不出的一律返回 null（前端渲染成「未记录」），**不要兜底成 REAL**——
 * 那正是本次要根除的"未知值静默伪装成真实"。
 */
export function providerModeFromChannel(channel) {
    if (!channel)
        return null;
    const c = channel.toLowerCase();
    if (c.includes('sandbox'))
        return ProviderMode.SANDBOX;
    if (c.includes('pdf-text') || c.includes('local'))
        return ProviderMode.LOCAL;
    if (c.includes('manual'))
        return ProviderMode.MANUAL;
    if (c.includes('unavailable'))
        return ProviderMode.UNAVAILABLE;
    // 阿里云/乐企等真实通道
    if (c.includes('aliyun') || c.includes('leqi') || c.includes('qcc'))
        return ProviderMode.REAL;
    return null;
}
/** 外部调用结果状态 */
export const ProviderCallStatus = {
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    /** 超时 */
    TIMEOUT: 'TIMEOUT',
    /** 被限流 */
    THROTTLED: 'THROTTLED',
    /** 因配额耗尽被平台拦截，未真正发出请求 */
    QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
    /** 熔断 */
    CIRCUIT_OPEN: 'CIRCUIT_OPEN',
};
/* ==========================================================================
 * 六之二、合同附件解析
 * ========================================================================== */
/* ==========================================================================
 * 合同全生命周期（需求文档第一部分）
 * ========================================================================== */
/**
 * 合同类型。**层级不单独存列**，由类型推导（见 `CONTRACT_LEVEL_OF`）——
 * 存一份就可能与类型不一致，而"这张合同到底是几级"处处要用。
 */
export const ContractType = {
    /** 总包合同（L1）：与甲方签订，项目源头 */
    GENERAL: 'GENERAL',
    /** 专业分包合同（L2） */
    SUBCONTRACT: 'SUBCONTRACT',
    /** 材料/设备采购合同（L2） */
    PURCHASE: 'PURCHASE',
    /** 设备/周转材料租赁合同（L2） */
    LEASE: 'LEASE',
    /** 检测/设计/咨询等服务合同（L2） */
    SERVICE: 'SERVICE',
    /** 补充协议（L3）：关联原合同，独立编号 */
    SUPPLEMENT: 'SUPPLEMENT',
};
export const CONTRACT_TYPE_LABELS = {
    GENERAL: '总包合同',
    SUBCONTRACT: '分包合同',
    PURCHASE: '采购合同',
    LEASE: '租赁合同',
    SERVICE: '服务合同',
    SUPPLEMENT: '补充协议',
};
export const ContractLevel = { L1: 'L1', L2: 'L2', L3: 'L3' };
export const CONTRACT_LEVEL_LABELS = {
    L1: '一级（总包）',
    L2: '二级（分包/采购/租赁/服务）',
    L3: '三级（补充协议）',
};
/** 合同层级由类型推导，不是第二个真相源 */
export const CONTRACT_LEVEL_OF = {
    GENERAL: ContractLevel.L1,
    SUBCONTRACT: ContractLevel.L2,
    PURCHASE: ContractLevel.L2,
    LEASE: ContractLevel.L2,
    SERVICE: ContractLevel.L2,
    SUPPLEMENT: ContractLevel.L3,
};
/** 对方单位是本企业的甲方还是乙方。总包合同的对方是建设单位（甲方），其余多是乙方 */
export const CounterpartyRole = {
    OWNER: 'OWNER',
    CONTRACTOR: 'CONTRACTOR',
};
export const COUNTERPARTY_ROLE_LABELS = {
    OWNER: '甲方（发包/建设单位）',
    CONTRACTOR: '乙方（分包/供应单位）',
};
export const SettlementMethod = {
    BY_PROGRESS: 'BY_PROGRESS',
    BY_MILESTONE: 'BY_MILESTONE',
    BY_MONTH: 'BY_MONTH',
};
export const SETTLEMENT_METHOD_LABELS = {
    BY_PROGRESS: '按进度',
    BY_MILESTONE: '按节点',
    BY_MONTH: '按月',
};
/**
 * 结算阶段（需求 3.1 的「预付款/进度款/结算款/质保金」）。
 *
 * 【与 `contract.settlement_stage` 的分工】
 * 那个自由文本列是**发票比对用**的"当前阶段标签"（一期就有，`matchContract` 在读），
 * 本枚举属于 `contract_stage` 表的**阶段计划**（金额、比例、到期日）。
 * 两者刻意不复用：一个是标签、一个是计划，合成一个会让发票比对的语义跟着付款排期漂。
 */
export const ContractSettlementStage = {
    ADVANCE: 'ADVANCE',
    PROGRESS: 'PROGRESS',
    SETTLEMENT: 'SETTLEMENT',
    RETENTION: 'RETENTION',
};
export const CONTRACT_SETTLEMENT_STAGE_LABELS = {
    ADVANCE: '预付款',
    PROGRESS: '进度款',
    SETTLEMENT: '结算款',
    RETENTION: '质保金',
};
/**
 * 合同状态。
 *
 * 【为什么 `ARCHIVED` 是终态而不是"已归档"标记列】
 * 归档要能从台账默认视图里退出，也要能被"档案检索"单独捞出来。
 * 用状态表达生命周期、而不是加一堆布尔列，是本仓库既有做法（见 `invoice_status`）。
 */
export const ContractStatus = {
    /** 草稿：已登记未提交审批 */
    DRAFT: 'DRAFT',
    /** 审批中 */
    APPROVING: 'APPROVING',
    /** 已生效（审批通过）。一期只有这一个状态 */
    ACTIVE: 'ACTIVE',
    /** 审批未通过 */
    REJECTED: 'REJECTED',
    /** 已作废 */
    TERMINATED: 'TERMINATED',
    /** 已归档 */
    ARCHIVED: 'ARCHIVED',
};
export const CONTRACT_STATUS_LABELS = {
    DRAFT: '草稿',
    APPROVING: '审批中',
    ACTIVE: '已生效',
    REJECTED: '已退回',
    TERMINATED: '已作废',
    ARCHIVED: '已归档',
};
export const ContractChangeType = {
    /** 变更：改原合同的金额/条款 */
    CHANGE: 'CHANGE',
    /** 补充协议：关联原合同、独立编号 */
    SUPPLEMENT: 'SUPPLEMENT',
};
export const CONTRACT_CHANGE_TYPE_LABELS = {
    CHANGE: '合同变更',
    SUPPLEMENT: '补充协议',
};
export const ContractChangeStatus = {
    DRAFT: 'DRAFT',
    APPROVING: 'APPROVING',
    /** 已生效并已改到合同主表上 */
    APPLIED: 'APPLIED',
    REJECTED: 'REJECTED',
};
export const CONTRACT_CHANGE_STATUS_LABELS = {
    DRAFT: '草稿',
    APPROVING: '审批中',
    APPLIED: '已生效',
    REJECTED: '已退回',
};
/**
 * 合同预警类型（需求 3.4）。
 *
 * 每一种都由 `jobs/contractAlerts.ts` 扫出来写进 `contract_alert`，
 * `dedup_key` 上唯一索引保证重跑不重复。
 */
export const ContractAlertType = {
    OVER_INVOICE: 'OVER_INVOICE',
    OVER_PAYMENT: 'OVER_PAYMENT',
    EXPIRING: 'EXPIRING',
    WARRANTY_EXPIRING: 'WARRANTY_EXPIRING',
    STAGE_OVER_RATIO: 'STAGE_OVER_RATIO',
    NEVER_INVOICED: 'NEVER_INVOICED',
    /**
     * 未关联上级合同。
     *
     * 【为什么这条预警存在】
     * 一期两张真实合同（一期轻量模型留下的）没有总包合同可挂，
     * 迁移**不回填**——编一个不存在的总包合同就是假数据。
     * 于是缺口用预警暴露出来，让人去补真实数据，而不是静默。
     */
    PARENT_MISSING: 'PARENT_MISSING',
    SUPPLIER_ABNORMAL: 'SUPPLIER_ABNORMAL',
};
export const CONTRACT_ALERT_TYPE_LABELS = {
    OVER_INVOICE: '超开票',
    OVER_PAYMENT: '超付款',
    EXPIRING: '临近到期',
    WARRANTY_EXPIRING: '质保金到期',
    STAGE_OVER_RATIO: '结算阶段超比例',
    NEVER_INVOICED: '长期未开票',
    PARENT_MISSING: '未关联上级合同',
    SUPPLIER_ABNORMAL: '供应商异常',
};
export const AlertStatus = {
    /** 待处理 */
    OPEN: 'OPEN',
    /** 已确认（问题属实，已知悉） */
    ACK: 'ACK',
    /** 已忽略（判定为误报，必须填理由） */
    DISMISSED: 'DISMISSED',
};
export const ALERT_STATUS_LABELS = {
    OPEN: '待处理',
    ACK: '已确认',
    DISMISSED: '已忽略',
};
export const ContractPaymentStatus = {
    APPLIED: 'APPLIED',
    APPROVED: 'APPROVED',
    PAID: 'PAID',
    VOID: 'VOID',
};
export const CONTRACT_PAYMENT_STATUS_LABELS = {
    APPLIED: '已申请',
    APPROVED: '已批准',
    PAID: '已付款',
    VOID: '已作废',
};
/** 履约评价等级（需求 3.3「供应商/分包商履约评价」） */
export const ContractEvaluationLevel = {
    A: 'A',
    B: 'B',
    C: 'C',
    D: 'D',
};
export const CONTRACT_EVALUATION_LEVEL_LABELS = {
    A: 'A 优秀',
    B: 'B 良好',
    C: 'C 一般',
    D: 'D 较差',
};
/* ==========================================================================
 * 费用报销（需求文档第二部分）
 * ========================================================================== */
export const ExpenseType = {
    TRAVEL: 'TRAVEL',
    ENTERTAIN: 'ENTERTAIN',
    OFFICE: 'OFFICE',
    COMM: 'COMM',
    TRANSPORT: 'TRANSPORT',
    TRAINING: 'TRAINING',
    LABOR: 'LABOR',
    OTHER: 'OTHER',
};
export const EXPENSE_TYPE_LABELS = {
    TRAVEL: '差旅费',
    ENTERTAIN: '招待费',
    OFFICE: '办公费',
    COMM: '通讯费',
    TRANSPORT: '交通费',
    TRAINING: '培训费',
    LABOR: '劳保费',
    OTHER: '其他',
};
/**
 * 哪些费用类型必须有发票（需求 2 的"是否需发票"列，只有「其他」是视情况）。
 *
 * 单独导出成一张表而不是散在各处判断：漏掉一处就会出现
 * "某类费用没票也能过初审"，而这类疏漏在界面上看不出来。
 */
export const EXPENSE_TYPE_REQUIRES_INVOICE = {
    TRAVEL: true,
    ENTERTAIN: true,
    OFFICE: true,
    COMM: true,
    TRANSPORT: true,
    TRAINING: true,
    LABOR: true,
    OTHER: false,
};
/**
 * 发票品类 → 费用类型的匹配词表（需求 3.3「费用类型匹配」）。
 *
 * 用于判断"这张餐饮票报成差旅费"这类错配。**只做提示、不做拦截**——
 * 与本仓库"自动化只做风险标记"的取向一致；而且词表不可能穷尽，
 * 拦错了反而挡住真实业务。
 */
export const EXPENSE_TYPE_CATEGORY_HINTS = {
    TRAVEL: ['住宿', '机票', '车票', '火车', '客运', '航空', '差旅', '宾馆', '酒店'],
    ENTERTAIN: ['餐饮', '餐费', '食品', '酒', '茶', '招待'],
    OFFICE: ['办公', '文具', '耗材', '纸张', '打印', '电脑', '设备'],
    COMM: ['通信', '通讯', '电话', '电信', '移动', '联通', '宽带', '网络'],
    TRANSPORT: ['加油', '燃油', '过路', '通行', '停车', '油费', '运输', '车辆', '维修'],
    TRAINING: ['培训', '会议', '会务', '教育', '咨询'],
    LABOR: ['劳保', '防护', '手套', '工作服', '安全帽', '用品'],
    OTHER: [],
};
export const ExpenseApplyStatus = {
    DRAFT: 'DRAFT',
    APPROVING: 'APPROVING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
    /** 已报销完毕/不再使用 */
    CLOSED: 'CLOSED',
};
export const EXPENSE_APPLY_STATUS_LABELS = {
    DRAFT: '草稿',
    APPROVING: '审批中',
    APPROVED: '已批准',
    REJECTED: '已退回',
    CANCELLED: '已撤销',
    CLOSED: '已关闭',
};
export const ExpenseReimburseStatus = {
    DRAFT: 'DRAFT',
    APPROVING: 'APPROVING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    PAID: 'PAID',
    ARCHIVED: 'ARCHIVED',
    CANCELLED: 'CANCELLED',
};
export const EXPENSE_REIMBURSE_STATUS_LABELS = {
    DRAFT: '草稿',
    APPROVING: '审批中',
    APPROVED: '已通过',
    REJECTED: '已退回',
    PAID: '已付款',
    ARCHIVED: '已归档',
    CANCELLED: '已撤销',
};
export const ExpensePaymentMethod = {
    BANK: 'BANK',
    CASH: 'CASH',
    CARD: 'CARD',
    OTHER: 'OTHER',
};
export const EXPENSE_PAYMENT_METHOD_LABELS = {
    BANK: '银行转账',
    CASH: '现金',
    CARD: '公务卡',
    OTHER: '其他',
};
export const ExpensePaymentStatus = {
    PENDING: 'PENDING',
    PAID: 'PAID',
    FAILED: 'FAILED',
};
export const EXPENSE_PAYMENT_STATUS_LABELS = {
    PENDING: '待付款',
    PAID: '已付款',
    FAILED: '付款失败',
};
export const ExpenseAlertType = {
    BUDGET_WARN: 'BUDGET_WARN',
    BUDGET_EXCEEDED: 'BUDGET_EXCEEDED',
    PENDING_TOO_LONG: 'PENDING_TOO_LONG',
    INVOICE_RELEASED: 'INVOICE_RELEASED',
};
export const EXPENSE_ALERT_TYPE_LABELS = {
    BUDGET_WARN: '预算接近上限',
    BUDGET_EXCEEDED: '预算已超支',
    PENDING_TOO_LONG: '单据长期未处理',
    INVOICE_RELEASED: '发票被释放',
};
export const BudgetPeriodType = {
    MONTH: 'MONTH',
    QUARTER: 'QUARTER',
    YEAR: 'YEAR',
};
export const BUDGET_PERIOD_TYPE_LABELS = {
    MONTH: '按月',
    QUARTER: '按季',
    YEAR: '按年',
};
export const BudgetStatus = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    CLOSED: 'CLOSED',
};
export const BUDGET_STATUS_LABELS = {
    DRAFT: '草稿',
    ACTIVE: '执行中',
    CLOSED: '已关闭',
};
export const BudgetAdjustStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const BUDGET_ADJUST_STATUS_LABELS = {
    PENDING: '待审批',
    APPROVED: '已批准',
    REJECTED: '已驳回',
};
/**
 * 超预算时的处置方式（需求 3.6「超 80% 提醒，超 100% 拦截（可配置）」）。
 *
 * 【默认为什么是 WARN 而不是 BLOCK】
 * 与本仓库"自动化只做风险标记，不跳过人工确认"的取向一致：
 * 预算数字本身可能滞后（在途单据还没入账），默认硬拦会挡住真实业务。
 * 要拦得由租户在配置里显式选择。
 */
export const BudgetOverPolicy = {
    WARN: 'WARN',
    BLOCK: 'BLOCK',
};
export const BUDGET_OVER_POLICY_LABELS = {
    WARN: '只预警，不拦截',
    BLOCK: '超预算时拦截',
};
/**
 * 发票的业务来源。
 *
 * 【为什么需要这一列】
 * 报销上传的发票要复用发票表的 OCR/企查查/验真管道与风险项，
 * 但它**不属于发票业务**：不进财务初审工作台、不算首页指标、
 * 不启专用流程。不给它一个可过滤的标记，它就会混进所有发票查询里——
 * 不报错，只是数字变大（这是本仓库反复出现的那类静默故障）。
 */
export const InvoiceSource = {
    INVOICE: 'INVOICE',
    EXPENSE: 'EXPENSE',
};
export const INVOICE_SOURCE_LABELS = {
    INVOICE: '发票业务',
    EXPENSE: '费用报销',
};
/** 附件的实际文件类型。**由服务端嗅探魔术字节得出**，不采信客户端的 MIME 与扩展名 */
export const ContractFileKind = {
    PDF: 'PDF',
    DOCX: 'DOCX',
    /** 图片（jpg/png/bmp/tiff/webp），一律走扫描件识别通道 */
    IMAGE: 'IMAGE',
};
/**
 * 附件解析结果。**这是一个穷尽枚举**——前端遇到未知值必须渲染成灰色的
 * 「未知状态」，绝不允许兜底成"成功"。
 *
 * 这条规矩的代价是真实发生过的：沙箱伪造的校验结论一路显示为绿色「校验成功」，
 * 用户直到看见一条凭空捏造的风险才发现数据是假的。
 */
export const ContractExtractStatus = {
    /** 正文与表格都已提取 */
    OK: 'OK',
    /** 提取到了内容但有缺失（截断、部分页失败、文字乱码） */
    PARTIAL: 'PARTIAL',
    /**
     * PDF 没有文本层（扫描件）。**这不是失败**，是"需要走扫描件识别通道"
     * ——该通道未开通时会进一步标成 UNAVAILABLE。
     */
    NO_TEXT_LAYER: 'NO_TEXT_LAYER',
    /** 通道未开通/未配置。原件已保存，需人工录入 */
    UNAVAILABLE: 'UNAVAILABLE',
    /** PDF 有密码保护 */
    ENCRYPTED: 'ENCRYPTED',
    /** 文件损坏或格式异常，解析失败 */
    FAILED: 'FAILED',
    /** 解析超时 */
    TIMEOUT: 'TIMEOUT',
};
export const CONTRACT_EXTRACT_STATUS_LABELS = {
    OK: '已提取',
    PARTIAL: '部分提取',
    NO_TEXT_LAYER: '无文本层（疑为扫描件）',
    UNAVAILABLE: '通道未激活',
    ENCRYPTED: 'PDF 有密码',
    FAILED: '解析失败',
    TIMEOUT: '解析超时',
};
/* ==========================================================================
 * 六之三、发票附件与手写内容
 * ========================================================================== */
/**
 * 手写内容提取结果。**与合同解析共用同一条规矩**——穷尽枚举，
 * 前端遇到未知值必须渲染成灰色「未知状态」，绝不允许兜底成"成功"。
 *
 * 【为什么不复用 ContractExtractStatus】
 * 那套里 `NO_TEXT_LAYER`（PDF 无文本层）与 `ENCRYPTED`（PDF 有密码）
 * 是**合同版式解析**特有的分流语义；手写提取面对的主要输入是照片，
 * 多出来的是「格式不支持」（PDF 目前只存档、不识别）。
 * 两个枚举值集不同、含义不同，强行合并会让前端多出一堆用不到的分支。
 */
export const HandwritingStatus = {
    /** 已提取到手写内容 */
    OK: 'OK',
    /** 提取到内容但有缺失（部分行识别失败、超出长度上限被截断） */
    PARTIAL: 'PARTIAL',
    /**
     * 文件格式当前不支持手写提取（PDF）。
     *
     * **这不是失败**——原件已保存、可下载，只是手写提取需要一张图片。
     * 与 UNAVAILABLE 分开：那个是"通道没就绪"，这个是"这个文件类型不做"。
     */
    UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
    /** 通道未开通/未配置。原件已保存 */
    UNAVAILABLE: 'UNAVAILABLE',
    /** 文件损坏、图像无法解码等，提取失败 */
    FAILED: 'FAILED',
    /** 提取超时 */
    TIMEOUT: 'TIMEOUT',
};
export const HANDWRITING_STATUS_LABELS = {
    OK: '已提取手写内容',
    PARTIAL: '部分提取',
    UNSUPPORTED_FORMAT: '该格式不提取（仅支持图片）',
    UNAVAILABLE: '通道未激活',
    FAILED: '提取失败',
    TIMEOUT: '提取超时',
};
/* ==========================================================================
 * 业务对象类型（流程实例、T+ 同步、消息的多态关联键）
 * ========================================================================== */
/**
 * `flow_instance.biz_type` / `tplus_sync.biz_type` / `message.biz_type` 的取值。
 *
 * 【为什么要有一个集中的枚举】
 * 这几个字段都是**无外键的多态关联**，从前只有一个字面量 `'INVOICE'` 散落在
 * 十来处（`invoice.service.ts`、`invoice.repo.ts`、`tplus.service.ts`、
 * `message.service.ts`…）。再加四种业务对象后，靠字面量必然出现
 * "删除时漏清一类引用"（留下待办点进去 404）或"任务列表 JOIN 漏一类"
 * （非发票待办永远不出现）。集中一处，漏掉会被类型检查抓住。
 */
export const BizType = {
    INVOICE: 'INVOICE',
    CONTRACT: 'CONTRACT',
    /** 合同变更/补充协议的审批走独立实例，便于与原合同审批区分 */
    CONTRACT_CHANGE: 'CONTRACT_CHANGE',
    EXPENSE_APPLY: 'EXPENSE_APPLY',
    EXPENSE_REIMBURSE: 'EXPENSE_REIMBURSE',
};
export const BIZ_TYPE_LABELS = {
    INVOICE: '发票',
    CONTRACT: '合同',
    CONTRACT_CHANGE: '合同变更',
    EXPENSE_APPLY: '费用申请',
    EXPENSE_REIMBURSE: '费用报销',
};
/**
 * 走审批流程实例的业务类型——**只有这些能配流程模板**。
 *
 * `CONTRACT_CHANGE` 在 `BizType` 里，但合同变更目前不经流程实例（登记后直接生效），
 * 给它配模板会造出一个永远不会被选中的模板。服务端保存校验与前端的新建下拉
 * 共用这一份清单：两边各写一份的话，前端能选出服务端必然拒绝的类型。
 */
export const FLOW_BACKED_BIZ_TYPES = [
    BizType.INVOICE,
    BizType.CONTRACT,
    BizType.EXPENSE_APPLY,
    BizType.EXPENSE_REIMBURSE,
];
/* ==========================================================================
 * 七、T+ 同步
 * ========================================================================== */
export const TplusSyncStatus = {
    /** 待推送 */
    PENDING: 'PENDING',
    /** 推送中 */
    PUSHING: 'PUSHING',
    /** 已推送至 T+ 待办业务池，等待财务人员选科目生成凭证 */
    PUSHED: 'PUSHED',
    /** T+ 已生成凭证并回写凭证号 */
    BOOKED: 'BOOKED',
    /** 推送失败 */
    FAILED: 'FAILED',
    /**
     * 已推送，但超过轮询窗口仍未在 T+ 查到凭证，需人工核对。
     *
     * 【它不是失败】推送确实成功了，失败的是我们**在 T+ 侧确认凭证的能力**
     * （T+ 没有凭证类订阅消息，只能轮询；而「发票生凭证」生成的凭证
     * 大概率不携带我们的外部单号，导致关联不上）。
     * 与 FAILED 混为一谈会让财务以为是推送挂了，去查的方向完全不同。
     */
    UNCONFIRMED: 'UNCONFIRMED',
};
export const TPLUS_SYNC_STATUS_LABELS = {
    PENDING: '待推送',
    PUSHING: '推送中',
    PUSHED: '已推送待记账',
    BOOKED: '已记账',
    FAILED: '推送失败',
    UNCONFIRMED: '待人工核对',
};
/* ==========================================================================
 * 八、消息与待办
 * ========================================================================== */
export const MessageChannel = {
    /** 站内消息 */
    IN_APP: 'IN_APP',
    DINGTALK: 'DINGTALK',
    SMS: 'SMS',
};
export const MESSAGE_CHANNEL_LABELS = {
    IN_APP: '站内',
    DINGTALK: '钉钉',
    SMS: '短信',
};
export const MessageType = {
    /** 待办提醒 */
    TODO: 'TODO',
    /** 风险事件提醒 */
    RISK_ALERT: 'RISK_ALERT',
    /** 节点超时提醒 */
    TIMEOUT: 'TIMEOUT',
    /** 接口异常提醒 */
    PROVIDER_ERROR: 'PROVIDER_ERROR',
    /** 系统通知 */
    SYSTEM: 'SYSTEM',
    /** 合同审批待办 */
    CONTRACT_TODO: 'CONTRACT_TODO',
    /** 合同预警（超开票/超付款/临近到期等） */
    CONTRACT_ALERT: 'CONTRACT_ALERT',
    /** 费用申请/报销审批待办 */
    EXPENSE_TODO: 'EXPENSE_TODO',
    /** 预算预警 */
    BUDGET_ALERT: 'BUDGET_ALERT',
};
export const MESSAGE_TYPE_LABELS = {
    TODO: '待办',
    RISK_ALERT: '风险提醒',
    TIMEOUT: '超时提醒',
    PROVIDER_ERROR: '接口异常',
    SYSTEM: '系统通知',
    CONTRACT_TODO: '合同待办',
    CONTRACT_ALERT: '合同预警',
    EXPENSE_TODO: '报销待办',
    BUDGET_ALERT: '预算预警',
};
/* ==========================================================================
 * 九、审计
 * ========================================================================== */
/** 审计日志类别（文档 8.3 合规审计） */
export const AuditCategory = {
    /** 认证与授权 */
    AUTH: 'AUTH',
    /** 数据变更 */
    DATA_CHANGE: 'DATA_CHANGE',
    /** 外部接口调用 */
    PROVIDER_CALL: 'PROVIDER_CALL',
    /** 平台运营人员访问租户数据 */
    CROSS_TENANT_ACCESS: 'CROSS_TENANT_ACCESS',
    /** 敏感数据导出 */
    DATA_EXPORT: 'DATA_EXPORT',
    /** 配置变更 */
    CONFIG_CHANGE: 'CONFIG_CHANGE',
    /** 越权检测告警 */
    PRIVILEGE_VIOLATION: 'PRIVILEGE_VIOLATION',
};
export const AUDIT_CATEGORY_LABELS = {
    AUTH: '认证授权',
    DATA_CHANGE: '数据变更',
    PROVIDER_CALL: '接口调用',
    CROSS_TENANT_ACCESS: '跨租户访问',
    DATA_EXPORT: '数据导出',
    CONFIG_CHANGE: '配置变更',
    PRIVILEGE_VIOLATION: '越权告警',
};
/** 登录主体类型 */
export const PrincipalType = {
    /** 平台运营人员 */
    PLATFORM: 'PLATFORM',
    /** 租户用户 */
    TENANT: 'TENANT',
};
//# sourceMappingURL=enums.js.map